import { useEffect, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Popover } from 'react-tiny-popover';
import {
  MoreHorizontal,
  Pencil,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import type { GetUserFiltersQuery } from '~/graphql/graphql';

type UserFilter = GetUserFiltersQuery['userFilters'][number];

interface FilterSelectorProps {
  filters: UserFilter[];
  activeFilterId: string | null | undefined;
  onActivate: (filterId: string) => void;
  onClear: () => void;
  isLoading: boolean;
  onRenameFilter: (filterId: string, newName: string) => void;
  onEditFilter: (filter: UserFilter) => void;
  onDeleteFilter: (filterId: string) => void;
  deleteConfirmTitle: (name: string) => string;
  deleteConfirmButton: string;
  deleteCancelButton: string;
  ariaLabelContainer: string;
  ariaLabelRenameInput: string;
  ariaLabelFilterOptions: string;
  menuRenameLabel: string;
  menuEditLabel: string;
  menuDeleteLabel: string;
}

export function FilterSelector({
  filters,
  activeFilterId,
  onActivate,
  onClear,
  isLoading,
  onRenameFilter,
  onEditFilter,
  onDeleteFilter,
  deleteConfirmTitle,
  deleteConfirmButton,
  deleteCancelButton,
  ariaLabelContainer,
  ariaLabelRenameInput,
  ariaLabelFilterOptions,
  menuRenameLabel,
  menuEditLabel,
  menuDeleteLabel,
}: FilterSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const renameSubmittedRef = useRef(false);
  const [renamingFilterId, setRenamingFilterId] = useState<string | null>(null);
  const [deletingFilterId, setDeletingFilterId] = useState<string | null>(null);

  useEffect(() => {
    renameSubmittedRef.current = false;
  }, [renamingFilterId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
    // filters.length gates re-run: ref is null during loading skeleton, only attaches once filters arrive
  }, [filters.length]);

  const handleRenameSubmit = (filterId: string, value: string) => {
    const trimmed = value.trim();
    const currentFilter = filters.find((f) => f.id === filterId);
    if (!trimmed || trimmed === currentFilter?.name) {
      setRenamingFilterId(null);
      return;
    }
    onRenameFilter(filterId, trimmed);
    setRenamingFilterId(null);
  };

  if (isLoading) {
    return (
      <div
        className="flex-1 overflow-x-auto flex items-center gap-2 min-w-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        role="group"
        aria-label={ariaLabelContainer}
      >
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex-shrink-0 h-9 w-24 rounded-lg bg-neut-200 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!filters.length) return null;

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-x-auto flex items-center gap-2 min-w-[8rem] [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      role="group"
      aria-label={ariaLabelContainer}
    >
      {filters.map((filter) => {
        const isActive = filter.id === activeFilterId;

        if (renamingFilterId === filter.id) {
          return (
            <div
              key={filter.id}
              className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg border border-prim bg-neut-0 px-2 py-1 transition-colors whitespace-nowrap"
            >
              <input
                key={renamingFilterId}
                type="text"
                defaultValue={filter.name}
                maxLength={80}
                autoFocus
                aria-label={ariaLabelRenameInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    renameSubmittedRef.current = true;
                    handleRenameSubmit(filter.id, e.currentTarget.value);
                  }
                  if (e.key === 'Escape') {
                    renameSubmittedRef.current = true;
                    setRenamingFilterId(null);
                  }
                }}
                onBlur={(e) => {
                  if (renameSubmittedRef.current) return;
                  handleRenameSubmit(filter.id, e.target.value);
                }}
                className="min-w-[6rem] max-w-[12rem] text-sm text-neut-prim bg-transparent outline-none"
              />
            </div>
          );
        }

        return (
          <Popover
            key={filter.id}
            isOpen={deletingFilterId === filter.id}
            onClickOutside={() => setDeletingFilterId(null)}
            positions={['bottom', 'top']}
            align="start"
            containerStyle={{ zIndex: '50' }}
            content={
              <div className="bg-neut-0 border border-neut-200 rounded-lg shadow-drop px-2 py-2 min-w-[160px] max-w-[240px]">
                <p className="text-sm font-medium text-neut-prim mb-2">
                  {deleteConfirmTitle(filter.name)}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onDeleteFilter(filter.id);
                      setDeletingFilterId(null);
                    }}
                    className="flex-1 px-2 py-2 text-sm font-medium bg-error-600 text-neut-0 rounded-md hover:bg-error-700 transition-colors cursor-pointer"
                  >
                    {deleteConfirmButton}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingFilterId(null)}
                    className="flex-1 px-2 py-2 text-sm font-medium bg-neut-100 text-neut-700 border border-neut-200 rounded-md hover:bg-neut-hover transition-colors cursor-pointer"
                  >
                    {deleteCancelButton}
                  </button>
                </div>
              </div>
            }
          >
            <div
              className={
                isActive
                  ? 'flex-shrink-0 inline-flex items-center gap-0 rounded-lg border border-prim bg-prim text-neut-0 transition-colors whitespace-nowrap group'
                  : 'flex-shrink-0 inline-flex items-center gap-0 rounded-lg border border-neut-200 bg-neut-100 text-neut-700 transition-colors whitespace-nowrap group'
              }
            >
              <button
                type="button"
                aria-pressed={isActive}
                onClick={() => (isActive ? onClear() : onActivate(filter.id))}
                className={
                  isActive
                    ? 'px-4 py-2 text-sm font-medium hover:bg-prim-hover rounded-l-lg transition-colors cursor-pointer'
                    : 'px-4 py-2 text-sm font-medium hover:bg-neut-hover rounded-l-lg transition-colors cursor-pointer'
                }
              >
                {filter.name}
              </button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    aria-label={ariaLabelFilterOptions}
                    className={
                      isActive
                        ? 'w-6 self-stretch flex items-center justify-center text-neut-0/70 hover:text-neut-0 hover:bg-neut-0/20 rounded-r-lg transition-colors cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                        : 'w-6 self-stretch flex items-center justify-center text-neut-400 hover:text-neut-700 hover:bg-neut-200/60 rounded-r-lg transition-colors cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                    }
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="min-w-[128px] bg-neut-0 border border-neut-200 rounded-lg shadow-drop py-1 z-50"
                >
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-2 py-2 text-sm text-neut-700 hover:bg-neut-100 cursor-pointer outline-none select-none"
                    onSelect={() => setRenamingFilterId(filter.id)}
                  >
                    <Pencil size={14} />
                    {menuRenameLabel}
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-2 py-2 text-sm text-neut-700 hover:bg-neut-100 cursor-pointer outline-none select-none"
                    onSelect={() => onEditFilter(filter)}
                  >
                    <SlidersHorizontal size={14} />
                    {menuEditLabel}
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="my-1 border-t border-neut-200" />
                  <DropdownMenu.Item
                    className="flex items-center gap-2 px-2 py-2 text-sm text-error-600 hover:bg-error-100 cursor-pointer outline-none select-none"
                    onSelect={() =>
                      setTimeout(() => setDeletingFilterId(filter.id), 0)
                    }
                  >
                    <Trash2 size={14} />
                    {menuDeleteLabel}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
          </Popover>
        );
      })}
    </div>
  );
}
